import { useState } from 'react';
import {
  Gauge,
  Clock,
  MousePointer,
  Move,
  Server,
  Zap,
  Eye,
  Search,
  Shield,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Lock,
  Monitor,
  Globe,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileCode,
} from 'lucide-react';

interface CoreWebVitals {
  lcp?: number;
  fid?: number;
  cls?: number;
  ttfb?: number;
  inp?: number;
}

interface LighthouseScores {
  // All optional — the lighthouse Lambda writes only the categories requested
  // for the scan, so a page may have any subset (perf/CWV from PSI,
  // a11y/seo/bestPractices from local Lighthouse).
  performance?: number;
  accessibility?: number;
  seo?: number;
  bestPractices?: number;
}

interface PerformanceMetricsProps {
  coreWebVitals: CoreWebVitals;
  performanceScore?: number;
  lighthouseScores?: LighthouseScores;
  loading?: boolean;
}

type Rating = 'good' | 'needs-improvement' | 'poor';

interface VitalConfig {
  key: keyof CoreWebVitals;
  label: string;
  fullName: string;
  unit: string;
  icon: typeof Clock;
  thresholds: { good: number; needsImprovement: number };
  format: (value: number) => string;
  description: string;
  optimizations: string[];
}

const VITAL_CONFIGS: VitalConfig[] = [
  {
    key: 'lcp',
    label: 'LCP',
    fullName: 'Largest Contentful Paint',
    unit: 's',
    icon: Eye,
    thresholds: { good: 2500, needsImprovement: 4000 },
    format: (v) => (v / 1000).toFixed(2),
    description: 'Measures the time it takes for the largest content element to become visible.',
    optimizations: [
      'Optimize and compress images (use WebP/AVIF formats)',
      'Implement lazy loading for below-the-fold images',
      'Use a CDN to serve assets from edge locations',
      'Preload critical resources with <link rel="preload">',
      'Remove render-blocking CSS and JavaScript',
      'Optimize server response time (TTFB)',
    ],
  },
  {
    key: 'fid',
    label: 'FID',
    fullName: 'First Input Delay',
    unit: 'ms',
    icon: MousePointer,
    thresholds: { good: 100, needsImprovement: 300 },
    format: (v) => v.toFixed(0),
    description: 'Measures the time from first user interaction to browser response.',
    optimizations: [
      'Break up long JavaScript tasks into smaller chunks',
      'Use web workers for heavy computations',
      'Defer non-critical JavaScript execution',
      'Remove unused JavaScript and polyfills',
      'Minimize main thread work',
      'Implement code splitting',
    ],
  },
  {
    key: 'cls',
    label: 'CLS',
    fullName: 'Cumulative Layout Shift',
    unit: '',
    icon: Move,
    thresholds: { good: 0.1, needsImprovement: 0.25 },
    format: (v) => v.toFixed(3),
    description: 'Measures visual stability by tracking unexpected layout shifts.',
    optimizations: [
      'Always include width and height attributes on images/videos',
      'Reserve space for dynamic content (ads, embeds)',
      'Avoid inserting content above existing content',
      'Use CSS transform for animations instead of layout properties',
      'Preload web fonts and use font-display: swap',
      'Avoid FOUT/FOIT with proper font loading strategy',
    ],
  },
  {
    key: 'ttfb',
    label: 'TTFB',
    fullName: 'Time to First Byte',
    unit: 'ms',
    icon: Server,
    thresholds: { good: 800, needsImprovement: 1800 },
    format: (v) => v.toFixed(0),
    description: 'Measures the time from request to first byte of response.',
    optimizations: [
      'Optimize database queries and add proper indexing',
      'Implement server-side caching (Redis, Memcached)',
      'Use a CDN with edge caching',
      'Enable HTTP/2 or HTTP/3',
      'Reduce server-side processing time',
      'Use connection keep-alive',
    ],
  },
  {
    key: 'inp',
    label: 'INP',
    fullName: 'Interaction to Next Paint',
    unit: 'ms',
    icon: Zap,
    thresholds: { good: 200, needsImprovement: 500 },
    format: (v) => v.toFixed(0),
    description: 'Measures overall responsiveness to all user interactions.',
    optimizations: [
      'Optimize event handlers to be fast and non-blocking',
      'Use requestIdleCallback for non-urgent work',
      'Implement debouncing/throttling for frequent events',
      'Avoid layout thrashing in event handlers',
      'Use passive event listeners where appropriate',
      'Reduce JavaScript bundle size',
    ],
  },
];

// Best Practices categories based on Lighthouse audit groups
interface BestPracticesCategory {
  id: string;
  label: string;
  icon: typeof Lock;
  description: string;
  audits: {
    id: string;
    title: string;
    description: string;
    recommendation: string;
  }[];
}

const BEST_PRACTICES_CATEGORIES: BestPracticesCategory[] = [
  {
    id: 'security',
    label: 'Security',
    icon: Lock,
    description: 'Protect user data and ensure secure connections',
    audits: [
      {
        id: 'is-on-https',
        title: 'Uses HTTPS',
        description: 'All sites should be protected with HTTPS, even those that don\'t handle sensitive data.',
        recommendation: 'Migrate your site to HTTPS using a free SSL certificate from Let\'s Encrypt.',
      },
      {
        id: 'no-mixed-content',
        title: 'No mixed content',
        description: 'HTTPS pages should not load resources over HTTP, which weakens security.',
        recommendation: 'Update all resource URLs to use HTTPS, or use protocol-relative URLs.',
      },
      {
        id: 'no-vulnerable-libraries',
        title: 'No vulnerable JavaScript libraries',
        description: 'Some third-party scripts contain known security vulnerabilities.',
        recommendation: 'Update JavaScript libraries to their latest secure versions.',
      },
      {
        id: 'csp-xss',
        title: 'Content Security Policy',
        description: 'A strong CSP significantly reduces the risk of cross-site scripting (XSS) attacks.',
        recommendation: 'Implement a Content Security Policy header to restrict resource loading.',
      },
    ],
  },
  {
    id: 'user-experience',
    label: 'User Experience',
    icon: Monitor,
    description: 'Ensure a good experience across devices and browsers',
    audits: [
      {
        id: 'doctype',
        title: 'Page has HTML doctype',
        description: 'Specifying a doctype prevents the browser from switching to quirks mode.',
        recommendation: 'Add <!DOCTYPE html> at the beginning of your HTML document.',
      },
      {
        id: 'charset',
        title: 'Properly defines charset',
        description: 'A character encoding declaration helps browsers render text correctly.',
        recommendation: 'Add <meta charset="UTF-8"> in the <head> section.',
      },
      {
        id: 'image-aspect-ratio',
        title: 'Images have correct aspect ratio',
        description: 'Image display dimensions should match natural aspect ratio to prevent layout shifts.',
        recommendation: 'Always specify width and height attributes on images.',
      },
      {
        id: 'preload-lcp-image',
        title: 'Preload LCP image',
        description: 'Preloading the largest contentful paint image improves load time.',
        recommendation: 'Add <link rel="preload" as="image"> for your hero images.',
      },
    ],
  },
  {
    id: 'browser-compatibility',
    label: 'Browser Compatibility',
    icon: Globe,
    description: 'Avoid deprecated APIs and browser-specific issues',
    audits: [
      {
        id: 'deprecations',
        title: 'Avoids deprecated APIs',
        description: 'Deprecated APIs will eventually be removed from browsers.',
        recommendation: 'Replace deprecated APIs with their modern equivalents.',
      },
      {
        id: 'errors-in-console',
        title: 'No browser errors',
        description: 'Errors logged to the console indicate unresolved problems.',
        recommendation: 'Fix JavaScript errors and warnings shown in the browser console.',
      },
      {
        id: 'geolocation-on-start',
        title: 'No geolocation on page load',
        description: 'Users distrust sites that request location without context.',
        recommendation: 'Only request geolocation after a user interaction.',
      },
      {
        id: 'notification-on-start',
        title: 'No notifications on page load',
        description: 'Users are mistrustful of sites that request notifications without context.',
        recommendation: 'Request notification permission only after user engagement.',
      },
    ],
  },
];

function getRating(value: number, thresholds: { good: number; needsImprovement: number }): Rating {
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.needsImprovement) return 'needs-improvement';
  return 'poor';
}

function getRatingColor(rating: Rating): string {
  switch (rating) {
    case 'good':
      return 'text-status-success';
    case 'needs-improvement':
      return 'text-status-warning';
    case 'poor':
      return 'text-status-error';
  }
}

function getRatingBgColor(rating: Rating): string {
  switch (rating) {
    case 'good':
      return 'bg-status-success';
    case 'needs-improvement':
      return 'bg-status-warning';
    case 'poor':
      return 'bg-status-error';
  }
}

function getRatingLabel(rating: Rating): string {
  switch (rating) {
    case 'good':
      return 'Good';
    case 'needs-improvement':
      return 'Needs Improvement';
    case 'poor':
      return 'Poor';
  }
}

function getScoreColor(score: number): string {
  if (score >= 90) return 'text-status-success';
  if (score >= 50) return 'text-status-warning';
  return 'text-status-error';
}

function getScoreStrokeColor(score: number): string {
  if (score >= 90) return 'stroke-status-success';
  if (score >= 50) return 'stroke-status-warning';
  return 'stroke-status-error';
}

function CircularGauge({ score, size = 120 }: { score: number; size?: number }) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-light"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`transition-all duration-500 ${getScoreStrokeColor(score)}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-heading-1 font-bold ${getScoreColor(score)}`}>
          {score}
        </span>
        <span className="text-caption text-gray-medium">/ 100</span>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="card animate-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-gray-light rounded" />
        <div className="h-4 bg-gray-light rounded w-16" />
      </div>
      <div className="h-8 bg-gray-light rounded w-20 mb-2" />
      <div className="h-3 bg-gray-light rounded w-24" />
    </div>
  );
}

function SkeletonGauge() {
  return (
    <div className="animate-pulse flex flex-col items-center">
      <div className="w-[120px] h-[120px] rounded-full bg-gray-light" />
      <div className="h-4 bg-gray-light rounded w-32 mt-3" />
    </div>
  );
}

function VitalCard({
  config,
  value
}: {
  config: VitalConfig;
  value?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = config.icon;
  const hasValue = value !== undefined && value !== null;
  const rating = hasValue ? getRating(value, config.thresholds) : null;
  const needsOptimization = rating === 'needs-improvement' || rating === 'poor';

  const getTargetText = () => {
    const { good } = config.thresholds;
    if (config.key === 'lcp') return `Target: <${(good / 1000).toFixed(1)}s`;
    if (config.key === 'cls') return `Target: <${good}`;
    return `Target: <${good}${config.unit}`;
  };

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-card ${hasValue && rating ? getRatingBgColor(rating) + '/10' : 'bg-gray-light'}`}>
              <Icon
                size={20}
                className={hasValue && rating ? getRatingColor(rating) : 'text-gray-medium'}
              />
            </div>
            <div>
              <span className="text-body font-semibold">{config.label}</span>
              <p className="text-caption text-gray-medium">{config.fullName}</p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown size={16} className="text-gray-medium flex-shrink-0" />
          ) : (
            <ChevronRight size={16} className="text-gray-medium flex-shrink-0" />
          )}
        </div>

        {hasValue ? (
          <>
            <div className={`text-heading-2 font-bold ${getRatingColor(rating!)}`}>
              {config.format(value)}
              {config.unit && <span className="text-body text-gray-medium ml-1">{config.unit}</span>}
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className={`text-body-sm font-medium ${getRatingColor(rating!)}`}>
                {getRatingLabel(rating!)}
              </span>
              <span className="text-caption text-gray-medium">
                {getTargetText()}
              </span>
            </div>
          </>
        ) : (
          <div className="text-heading-3 text-gray-medium">N/A</div>
        )}
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-light space-y-4">
          {/* Description */}
          <div>
            <p className="text-body-sm text-gray-medium">
              {config.description}
            </p>
          </div>

          {/* Thresholds */}
          <div className="flex gap-4 text-caption">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-status-success" />
              <span className="text-gray-medium">Good: {config.key === 'lcp' ? `<${config.thresholds.good / 1000}s` : config.key === 'cls' ? `<${config.thresholds.good}` : `<${config.thresholds.good}${config.unit}`}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-status-warning" />
              <span className="text-gray-medium">Needs work: {config.key === 'lcp' ? `<${config.thresholds.needsImprovement / 1000}s` : config.key === 'cls' ? `<${config.thresholds.needsImprovement}` : `<${config.thresholds.needsImprovement}${config.unit}`}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-status-error" />
              <span className="text-gray-medium">Poor</span>
            </div>
          </div>

          {/* Optimization recommendations */}
          {needsOptimization && (
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb size={16} className="text-amber-600" />
                <span className="text-body-sm font-semibold text-amber-800">
                  Optimization Recommendations
                </span>
              </div>
              <ul className="space-y-1">
                {config.optimizations.slice(0, 4).map((opt, i) => (
                  <li key={i} className="text-caption text-amber-700 flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    {opt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Show all optimizations if rating is good */}
          {!needsOptimization && hasValue && (
            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-green-600" />
                <span className="text-body-sm font-medium text-green-800">
                  This metric is performing well!
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LighthouseCategory({
  label,
  score,
  icon: Icon,
}: {
  label: string;
  score: number;
  icon: typeof Gauge;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-light rounded-card">
      <Icon size={18} className={getScoreColor(score)} />
      <div className="flex-1">
        <div className="text-body-sm font-medium">{label}</div>
      </div>
      <div className={`text-body font-bold ${getScoreColor(score)}`}>
        {score}
      </div>
    </div>
  );
}

function BestPracticesCategoryCard({
  category,
  isExpanded,
  onToggle,
}: {
  category: BestPracticesCategory;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const Icon = category.icon;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-card bg-coral/10">
              <Icon size={20} className="text-coral" />
            </div>
            <div>
              <span className="text-body font-semibold">{category.label}</span>
              <p className="text-caption text-gray-medium">{category.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-caption text-gray-medium">
              {category.audits.length} checks
            </span>
            {isExpanded ? (
              <ChevronDown size={16} className="text-gray-medium flex-shrink-0" />
            ) : (
              <ChevronRight size={16} className="text-gray-medium flex-shrink-0" />
            )}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-light space-y-3">
          {category.audits.map((audit) => (
            <div key={audit.id} className="bg-gray-light/50 rounded-lg p-3">
              <div className="flex items-start gap-2 mb-1">
                <FileCode size={14} className="text-coral mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-body-sm font-semibold text-dark-navy">{audit.title}</h4>
                  <p className="text-caption text-gray-medium mt-1">{audit.description}</p>
                  <div className="mt-2 flex items-start gap-2 bg-white rounded p-2 border border-gray-200">
                    <Lightbulb size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-caption text-gray-dark">{audit.recommendation}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BestPracticesSection({ score }: { score?: number }) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const hasScore = score !== undefined && score !== null;

  const getScoreStatus = () => {
    if (!hasScore) return null;
    if (score >= 90) return { icon: CheckCircle2, color: 'text-status-success', bg: 'bg-green-50', border: 'border-green-100', label: 'Excellent' };
    if (score >= 50) return { icon: AlertTriangle, color: 'text-status-warning', bg: 'bg-amber-50', border: 'border-amber-100', label: 'Needs Improvement' };
    return { icon: XCircle, color: 'text-status-error', bg: 'bg-red-50', border: 'border-red-100', label: 'Poor' };
  };

  const status = getScoreStatus();

  return (
    <div className="space-y-6">
      {/* Header with score */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <h3 className="text-heading-3 font-semibold flex items-center gap-2">
            <Shield className="text-coral" size={24} />
            Best Practices
          </h3>
          {hasScore && (
            <div className="flex items-center gap-3">
              <div className={`text-heading-2 font-bold ${getScoreColor(score)}`}>
                {score}
                <span className="text-body text-gray-medium font-normal">/100</span>
              </div>
            </div>
          )}
        </div>

        {/* Score status banner */}
        {status && hasScore && (
          <div className={`flex items-center gap-3 p-3 rounded-lg ${status.bg} border ${status.border} mb-4`}>
            <status.icon size={20} className={status.color} />
            <div>
              <span className={`text-body-sm font-semibold ${status.color}`}>{status.label}</span>
              <p className="text-caption text-gray-dark">
                {score >= 90
                  ? 'Your site follows modern web best practices.'
                  : score >= 50
                    ? 'Some best practices could be improved for better security and UX.'
                    : 'Several best practices issues need attention.'}
              </p>
            </div>
          </div>
        )}

        <p className="text-body-sm text-gray-medium">
          Best Practices audits check for common issues related to security, user experience, and browser compatibility.
          These checks help ensure your site is trustworthy, secure, and works well across different browsers.
        </p>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        <h4 className="text-body font-semibold text-dark-navy">Audit Categories</h4>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {BEST_PRACTICES_CATEGORIES.map((category) => (
            <BestPracticesCategoryCard
              key={category.id}
              category={category}
              isExpanded={expandedCategories.has(category.id)}
              onToggle={() => toggleCategory(category.id)}
            />
          ))}
        </div>
      </div>

      {/* Note about detailed audit data */}
      <div className="card bg-blue-50 border border-blue-100">
        <div className="flex items-start gap-3">
          <FileCode size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-body-sm font-semibold text-blue-800">About Best Practices Scoring</h4>
            <p className="text-caption text-blue-700 mt-1">
              The Best Practices score is calculated from individual audits covering security, user experience,
              and browser compatibility. The categories above show common audit types. For detailed per-page
              audit results, consider running Google Lighthouse directly on specific pages.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PerformanceMetrics({
  coreWebVitals,
  performanceScore,
  lighthouseScores,
  loading = false,
}: PerformanceMetricsProps) {
  if (loading) {
    return (
      <div className="space-y-8">
        {/* Loading Performance Score */}
        <div className="card">
          <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
            <Gauge className="text-coral" size={24} />
            Performance Score
          </h3>
          <div className="flex justify-center">
            <SkeletonGauge />
          </div>
        </div>

        {/* Loading Core Web Vitals */}
        <div>
          <h3 className="text-heading-3 font-semibold mb-4 flex items-center gap-2">
            <Zap className="text-coral" size={24} />
            Core Web Vitals
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const hasPerformanceScore = performanceScore !== undefined && performanceScore !== null;
  const hasLighthouseScores = lighthouseScores !== undefined && lighthouseScores !== null;
  const hasAnyVitals = Object.values(coreWebVitals).some(v => v !== undefined && v !== null);

  return (
    <div className="space-y-8">
      {/* Performance Score with Circular Gauge */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
          <Gauge className="text-coral" size={24} />
          Performance Score
        </h3>
        <div className="flex flex-col lg:flex-row items-center gap-8">
          <div className="flex flex-col items-center">
            {hasPerformanceScore ? (
              <>
                <CircularGauge score={performanceScore} />
                <p className="text-body-sm text-gray-medium mt-3">Overall Performance</p>
              </>
            ) : (
              <div className="text-center">
                <div className="w-[120px] h-[120px] rounded-full border-8 border-gray-light flex items-center justify-center">
                  <span className="text-heading-3 text-gray-medium">N/A</span>
                </div>
                <p className="text-body-sm text-gray-medium mt-3">No data available</p>
              </div>
            )}
          </div>

          {/* Lighthouse Scores */}
          {hasLighthouseScores && (
            <div className="flex-1 w-full">
              <h4 className="text-body font-semibold mb-3 text-gray-medium">Lighthouse Categories</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(
                  [
                    { label: 'Performance', score: lighthouseScores.performance, icon: Zap },
                    { label: 'Accessibility', score: lighthouseScores.accessibility, icon: Eye },
                    { label: 'SEO', score: lighthouseScores.seo, icon: Search },
                    { label: 'Best Practices', score: lighthouseScores.bestPractices, icon: Shield },
                  ] as { label: string; score: number | undefined; icon: typeof Zap }[]
                )
                  // Only show categories we actually have a score for.
                  .filter(
                    (c): c is { label: string; score: number; icon: typeof Zap } =>
                      c.score !== undefined && c.score !== null
                  )
                  .map((c) => (
                    <LighthouseCategory key={c.label} label={c.label} score={c.score} icon={c.icon} />
                  ))}
              </div>
            </div>
          )}

          {!hasLighthouseScores && hasPerformanceScore && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-medium">
                <Sparkles size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-body-sm">Lighthouse scores not available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Core Web Vitals */}
      <div>
        <h3 className="text-heading-3 font-semibold mb-4 flex items-center gap-2">
          <Zap className="text-coral" size={24} />
          Core Web Vitals
        </h3>

        {hasAnyVitals ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {VITAL_CONFIGS.map((config) => (
              <VitalCard
                key={config.key}
                config={config}
                value={coreWebVitals[config.key]}
              />
            ))}
          </div>
        ) : (
          <div className="card text-center py-8">
            <Clock size={48} className="mx-auto text-gray-medium opacity-50 mb-4" />
            <p className="text-body text-gray-medium">No Core Web Vitals data available</p>
            <p className="text-body-sm text-gray-medium mt-1">
              Run a performance scan to collect metrics
            </p>
          </div>
        )}
      </div>

      {/* Best Practices Section */}
      {hasLighthouseScores && (
        <BestPracticesSection score={lighthouseScores.bestPractices} />
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-body-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-success" />
          <span className="text-gray-medium">Good</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-warning" />
          <span className="text-gray-medium">Needs Improvement</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-error" />
          <span className="text-gray-medium">Poor</span>
        </div>
      </div>
    </div>
  );
}
